/**
 * Profile API Service
 *
 * Handles CRUD operations for node and switch profiles
 */

import { apiClient } from './client';
import type { NodeProfile } from '../../types/additional-nodes';
import type { SwitchModel } from '../../types/switches';
import type { CustomNodeProfile, CustomSwitchProfile, ProfileFilters, PaginatedResponse } from './types';

// ===== Node Profile API Methods =====

/**
 * Get all node profiles with optional filters
 */
export async function getNodeProfiles(filters?: ProfileFilters): Promise<NodeProfile[]> {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.role) queryParams.append('role', filters.role);
    if (filters.vendor) queryParams.append('vendor', filters.vendor);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
  }

  const query = queryParams.toString();
  const endpoint = query ? `/profiles/nodes?${query}` : '/profiles/nodes';

  return apiClient.get<NodeProfile[]>(endpoint);
}

/**
 * Get a specific node profile by ID
 */
export async function getNodeProfile(id: string): Promise<CustomNodeProfile> {
  return apiClient.get<CustomNodeProfile>(`/profiles/nodes/${id}`);
}

/**
 * Create a new node profile
 */
export async function createNodeProfile(profile: Omit<NodeProfile, 'id'>): Promise<CustomNodeProfile> {
  return apiClient.post<CustomNodeProfile>('/profiles/nodes', profile);
}

/**
 * Update an existing node profile
 */
export async function updateNodeProfile(id: string, profile: Partial<NodeProfile>): Promise<CustomNodeProfile> {
  return apiClient.put<CustomNodeProfile>(`/profiles/nodes/${id}`, profile);
}

/**
 * Delete a node profile
 */
export async function deleteNodeProfile(id: string): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`/profiles/nodes/${id}`);
}

// ===== Switch Profile API Methods =====

/**
 * Get all switch profiles with optional filters
 */
export async function getSwitchProfiles(filters?: ProfileFilters): Promise<SwitchModel[]> {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.vendor) queryParams.append('vendor', filters.vendor);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
  }

  const query = queryParams.toString();
  const endpoint = query ? `/profiles/switches?${query}` : '/profiles/switches';

  return apiClient.get<SwitchModel[]>(endpoint);
}

/**
 * Get a specific switch profile by ID
 */
export async function getSwitchProfile(id: string): Promise<CustomSwitchProfile> {
  return apiClient.get<CustomSwitchProfile>(`/profiles/switches/${id}`);
}

/**
 * Create a new switch profile
 */
export async function createSwitchProfile(profile: Omit<SwitchModel, 'id'>): Promise<CustomSwitchProfile> {
  return apiClient.post<CustomSwitchProfile>('/profiles/switches', profile);
}

/**
 * Update an existing switch profile
 */
export async function updateSwitchProfile(id: string, profile: Partial<SwitchModel>): Promise<CustomSwitchProfile> {
  return apiClient.put<CustomSwitchProfile>(`/profiles/switches/${id}`, profile);
}

/**
 * Delete a switch profile
 */
export async function deleteSwitchProfile(id: string): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`/profiles/switches/${id}`);
}

// ===== Bulk Operations =====

/**
 * Import multiple node profiles
 */
export async function importNodeProfiles(profiles: NodeProfile[]): Promise<{ imported: number; failed: number }> {
  return apiClient.post<{ imported: number; failed: number }>('/profiles/nodes/import', { profiles });
}

/**
 * Import multiple switch profiles
 */
export async function importSwitchProfiles(profiles: SwitchModel[]): Promise<{ imported: number; failed: number }> {
  return apiClient.post<{ imported: number; failed: number }>('/profiles/switches/import', { profiles });
}

/**
 * Export all custom profiles
 */
export async function exportAllProfiles(): Promise<{ nodes: NodeProfile[]; switches: SwitchModel[] }> {
  return apiClient.get<{ nodes: NodeProfile[]; switches: SwitchModel[] }>('/profiles/export');
}
